import type { DeepReadonly } from 'vue'
import type { GraphqlResponse } from '#graphql-middleware-server-options-build'
import type {
  AddToCartMutation,
  CartFragment,
  OrderItemFragment,
  PromotionFragment,
  RemoveFromCartMutation,
} from '#graphql-operations'
import { falsy } from '@/helpers/type'

type CartMutationResult = {
  errors?: string[]
}

type Cart = {
  orderItems: OrderItemFragment[]
}

type UseCart = {
  /**
   * Add a product with the given SKU to the cart.
   */
  add: (sku: string) => Promise<CartMutationResult>

  /**
   * Remove the order item with the given ID from the cart.
   */
  remove: (orderItemId: string) => Promise<CartMutationResult>

  /**
   * Load the cart. The cart is loaded initially when the app is mounted and
   * is updated through mutations. It should not be needed to call this method
   * manually.
   */
  load: () => Promise<void>

  /**
   * Get an order item from the cart for the given SKU.
   */
  getOrderItemForSku: (sku: string) => OrderItemFragment | undefined

  /**
   * The amount of order items in the cart.
   */
  count: ComputedRef<number>

  /**
   * Promotions that are available but didcar not apply to this order.
   */
  promotion: DeepReadonly<Ref<PromotionFragment | null>>

  /**
   * Order item with a promotion that was added to the cart.
   */
  orderItemPromotion: DeepReadonly<Ref<OrderItemFragment | null>>
}

export default function (): UseCart {
  const country = useCurrentCountry()

  const cart = useState<Cart>('cart', () => {
    return {
      orderItems: [],
    }
  })
  const promotion = useState<PromotionFragment | null>(
    'cartPromotion',
    () => null,
  )
  const orderItemPromotion = useState<OrderItemFragment | null>(
    'orderItemPromotion',
    () => null,
  )

  let previousOrderItems: OrderItemFragment[] = []

  const { messages } = useDrupalMessages()

  // Updates the cart state from either the load query or from a mutation.
  const updateCart = (v: CartFragment | null | undefined) => {
    // Store previous order items for comparison
    previousOrderItems = JSON.parse(JSON.stringify(cart.value.orderItems))

    // Make sure the orderItem Promotion is reset before evaluating if there was one added.
    orderItemPromotion.value = null

    cart.value.orderItems = (v?.orderItems || []).filter(falsy)
    promotion.value = v?.banners ? v.banners[0] : null

    // Check for new order items with promotions
    if (cart.value.orderItems.length > previousOrderItems.length) {
      const newOrderItems = cart.value.orderItems.filter(
        (item) =>
          !previousOrderItems.some(
            (prevItem) => prevItem.orderItemId === item.orderItemId,
          ),
      )

      // Find the first new order item with a promotion
      const newOrderItemWithPromotion = newOrderItems.find(
        (item) => item.promotion?.fieldCartBanner,
      )

      if (newOrderItemWithPromotion) {
        orderItemPromotion.value = newOrderItemWithPromotion
      }
    }
  }

  // Handles updating the state from a mutation.
  const handleMutation = (
    data: GraphqlResponse<AddToCartMutation | RemoveFromCartMutation>,
    errorMessage?: string,
  ) => {
    const order = data.data.mutation?.order
    const errors = data.data.mutation?.errors ?? data.errors

    if (order) {
      updateCart(order)
    } else if (errors?.length && errorMessage && messages.value.length === 0) {
      // only add this error messsage if no other error has been added yet.
      messages.value.push({ type: 'error', message: errorMessage })
    }

    return {
      errors: errors ?? [],
    }
  }

  const add = (sku: string) =>
    useGraphqlMutation('addToCart', {
      skus: [sku],
      country: country.value,
    }).then((data) =>
      handleMutation(data, 'Error: Could not add ' + sku + ' to your cart.'),
    )

  const remove = (id: string) =>
    useGraphqlMutation('removeFromCart', {
      id,
      country: country.value,
    }).then(handleMutation)

  const load = async () => {
    cart.value.orderItems = []
    const data = await useGraphqlQuery('cart').then(
      (v) => v.data.commerceGetCart?.order,
    )
    updateCart(data)
  }

  const getOrderItemForSku = (sku: string) =>
    cart.value.orderItems.find((v) => v.purchasedEntity?.sku === sku)

  const count = computed(() => cart.value.orderItems.length)

  return {
    add,
    remove,
    load,
    getOrderItemForSku,
    promotion: readonly(promotion),
    orderItemPromotion: readonly(orderItemPromotion),
    count,
  }
}
